import React, { useState } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { Button, withStyles, withModal } from "components/common";
import moment from "moment";
import { REMOVE_RELINK_REQUIRED_FLAG, UPDATE_AUTOPAY } from "utils/mutations";
import { useMutation } from "@apollo/react-hooks";
import { BANK_ACCOUNT_STATUS } from "utils/constants";
import EnableFinicityForUserButton from "./EnableFinicityForUserButton";

const AccountsList = ({
  user,
  refetch,
  openModal,
  transactionReport = {},
  fetchBalances = () => {},
  balancesLoading = true,
  agent_email,
}) => {
  let autopayInitialButtonDisabled = true;
  let autopayInitialButtonName = "Disable Autopay";
  if (user.tags) {
    autopayInitialButtonName =
      user.tags.includes("stop_all_pay") ||
      user?.tags.includes("stop_weekly_pay")
        ? "Enable Autopay"
        : "Disable Autopay";

    autopayInitialButtonDisabled = false;
  }

  const [autopayButtonName, setAutopayButtonName] = useState(
    autopayInitialButtonName,
  );
  const [autopayButtonDisabled, setAutopayButtonDisabled] = useState(
    autopayInitialButtonDisabled,
  );
  const [removeRelinkButtonDisabled, setRemoveRelinkButtonDisabled] = useState(
    false,
  );
  const [removeRelinkButtonName, setRemoveRelinkButtonName] = useState(
    "Remove Relink Flag",
  );
  const [updateAutopay] = useMutation(UPDATE_AUTOPAY);
  const [removeRelinkRequiredFlag] = useMutation(REMOVE_RELINK_REQUIRED_FLAG);

  const [errorMessage, setErrorMessage] = useState("");

  const toggleAutopayHandler = async () => {
    let res;
    try {
      res = await updateAutopay({
        variables: {
          email: user.email,
          agent_email,
        },
      });
    } catch (e) {
      setErrorMessage(
        `There was an error toggling autopay for ${user.email}. Error: ${e}`,
      );
    }
    if (res) {
      setAutopayButtonName(
        res.data?.updateAutopay?.message === "autopay ON"
          ? "Disable Autopay"
          : "Enable Autopay",
      );
      setAutopayButtonDisabled(false);
      refetch();
    } else {
      setAutopayButtonDisabled(true);
    }
  };

  const removeRelinkFlagHandler = async () => {
    let res;
    try {
      res = await removeRelinkRequiredFlag({
        variables: {
          email: user.email,
          agent_email,
        },
      });
    } catch (e) {
      setErrorMessage(
        `There was an error removing the relink flag for ${user.email}. Error: ${e}`,
      );
    }
    if (res) {
      setRemoveRelinkButtonName("Relink flag removed");
      setRemoveRelinkButtonDisabled(true);
    }
  };

  const linked_banks_table_rows = (user.bank_accounts || []).map((ba) => {
    const is_default_payment_method =
      ba.payment_method_id === user.default_payment_method.id && true;

    ba.is_default_payment_method = is_default_payment_method;

    const default_indicator = is_default_payment_method ? "> " : "";
    const default_label = is_default_payment_method ? " (default)" : "";

    ba.balances.sort((a, b) => b.created_at - a.created_at);

    return (
      <tr className="hover:bg-gray-300 odd:bg-gray-100" key={ba.account_id || `acct-${ba.lastFour}`}>
        <td>
          {default_indicator} {ba.institution} {default_label}
        </td>
        <td>{ba.name}</td>
        <td>{ba.lastFour}</td>
        <td>${ba.balances?.[0]?.current_balance || 0}</td>
        <td>{ba.status}</td>
        <td className={ba.unlinked ? "text-red-500" : ""}>
          {ba.unlinked ? "No" : "Yes"}
        </td>
        <td>
          {ba.status !== BANK_ACCOUNT_STATUS.NO_PAYMENT_METHOD && (
            <button
              className="btn-green p-1 text-white rounded-lg outline-0"
              onClick={(e) => {
                openModal(e, "ManageBankAccountModal", {
                  bankAccount: ba,
                  user,
                  refetch,
                });
              }}
            >
                Manage
            </button>
          )}
        </td>
      </tr>
    );
  });

  // list all asset reports with latest balances
  const assets = [];
  const { report } = user.asset_report || {};
  const { items, date_generated } = report || {};
  if (report && items) {
    items.forEach((ai) => {
      if (ai.accounts) {
        ai.accounts.forEach((a) => {
          assets.push(
            <div key={a.account_id}>
              <span className="font-bold">${a.balances.current}</span>
              <span>{` - ${a.name} (${a.type}/${a.subtype})`}</span>
            </div>,
          );
        });
      }
    });
  }

  const { institutions, createdDate = 0 } = transactionReport || {};
  if (institutions && institutions.length) {
    institutions.forEach((i) => {
      (i.accounts || []).forEach((a) => {
        assets.push(
          <div key={a.id}>
            <span className="font-bold">${a.balance}</span>
            <span>{` - ${a.name} (${a.type}/finicity)`}</span>
          </div>,
        );
      });
    });
  }
  const reportDate = date_generated || createdDate * 1000;
  return (
    <>
      <div className="mb-5">
        <h1 className="text-2xl font-title mb-2 font-light">
          Bank Accounts
          <span className="mx-5 text-base text-green-primary">
            <button
              onClick={async (e) => {
                fetchBalances({
                  variables: { userId: user.email },
                });
              }}
              className={
                balancesLoading
                  ? "bg-transparent hover:text-black"
                  : "hover:text-black"
              }
              disabled={balancesLoading}
            >
              Fetch Balances
            </button>
            <button
              type="button"
              style={{ outline: 0 }}
              className="btn-green w-16 text-white font-bold rounded-lg outline-0 mx-2"
              onClick={(e) =>
                openModal(e, "AddAccountModal", {
                  user,
                  refetch,
                })
              }
            >
              Add
            </button>
            <EnableFinicityForUserButton user={user} />
            {balancesLoading ? (
              <i className="fas fa-sync fa-spin ml-2" />
            ) : (
              <></>
            )}
          </span>
        </h1>
        <table className="tomo-table">
          <thead>
            <tr>
              <th>Bank Institution</th>
              <th>Account Type</th>
              <th>Account # Last 4</th>
              <th>Balance</th>
              <th>Status</th>
              <th>Linked</th>
              <th>Manage</th>
            </tr>
          </thead>
          <tbody>{linked_banks_table_rows}</tbody>
        </table>
        <div className="flex">
          <Button
            className="btn-green mt-2"
            onClick={async () => {
              await toggleAutopayHandler();
            }}
            disabled={autopayButtonDisabled}
          >
            {autopayButtonName}
          </Button>

          <Button
            className="btn-green mt-2 ml-2"
            onClick={async () => {
              await removeRelinkFlagHandler();
            }}
            disabled={removeRelinkButtonDisabled}
          >
            {removeRelinkButtonName}
          </Button>
          <div className={errorMessage ? "text-red-600 p-2" : "hidden"}>
            {errorMessage}
          </div>
        </div>
      </div>
      {assets.length ? (
        <div className="mb-5">
          <h1 className="text-2xl font-title font-light mb-2">
            Asset Report Accounts
            <span className="mb-2 text-gray-500 text-base">
              &nbsp;&nbsp; ( at time of application: &nbsp;&nbsp;
              <span className="text-black">
                {reportDate
                  ? moment(reportDate).format("MM/DD/YYYY")
                  : "unknown"}
              </span>
              )
            </span>
          </h1>
          <div>{assets}</div>
        </div>
      ) : (
        <></>
      )}
    </>
  );
};
const mapStateToProps = (state) => ({
  agent_email: state.user.id,
});

export default compose(
  connect(mapStateToProps),
  withStyles,
  withModal,
)(AccountsList);
